import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/feature/SeoHead';

const allServices = [
  {
    icon: 'ri-alarm-warning-fill',
    title: 'Emergency Cleanup',
    desc: 'Immediate 24/7 response for water, fire, and storm damage. Our crews arrive within 60 minutes to contain damage and begin the cleanup process.',
    features: ['Water extraction', 'Board-up & tarping', 'Debris removal', 'Hazard containment'],
    path: '/emergency-cleanup',
    img: 'https://public.readdy.ai/ai/img_res/d4dc9e75572ec701c09f990cf5e335d3.jpg',
    color: 'from-red-600 to-red-800',
  },
  {
    icon: 'ri-tools-fill',
    title: 'Property Restoration',
    desc: 'Complete structural and cosmetic restoration from pre-loss condition. We handle everything from drywall replacement to full interior rebuilds.',
    features: ['Structural drying', 'Drywall & flooring', 'Painting & finishing', 'Full rebuild services'],
    path: '/property-restoration',
    img: 'https://readdy.ai/api/search-image?query=property%20restoration%20workers%20repairing%20and%20rebuilding%20interior%20walls%20and%20flooring%20after%20water%20damage%2C%20professional%20construction%20team%2C%20clean%20bright%20workspace&width=800&height=500&seq=allsvc002&orientation=landscape',
    color: 'from-amber-600 to-amber-800',
  },
  {
    icon: 'ri-drop-fill',
    title: 'Pipe Leak & Drainage',
    desc: 'Expert pipe leak detection, repair, and comprehensive drainage cleaning to prevent future water damage and maintain proper flow.',
    features: ['Leak detection', 'Pipe repair & replacement', 'Drain cleaning', 'Hydro-jetting'],
    path: '/pipe-leak-drainage',
    img: 'https://readdy.ai/api/search-image?query=plumber%20technician%20repairing%20pipe%20leak%20under%20sink%20with%20professional%20tools%2C%20drainage%20cleaning%20equipment%2C%20clean%20white%20background%2C%20professional%20service&width=800&height=500&seq=allsvc003&orientation=landscape',
    color: 'from-teal-600 to-teal-800',
  },
  {
    icon: 'ri-search-eye-fill',
    title: 'Damage Assessment',
    desc: 'Comprehensive property inspection using thermal imaging and moisture detection technology to identify all damage before restoration begins.',
    features: ['Thermal imaging', 'Moisture mapping', 'Structural evaluation', 'Detailed reporting'],
    path: '/services',
    img: 'https://readdy.ai/api/search-image?query=professional%20inspector%20using%20thermal%20imaging%20camera%20to%20assess%20water%20damage%20in%20building%2C%20moisture%20detection%20equipment%2C%20clean%20professional%20environment&width=800&height=500&seq=allsvc004&orientation=landscape',
    color: 'from-indigo-600 to-indigo-800',
  },
  {
    icon: 'ri-lungs-fill',
    title: 'Mold Remediation',
    desc: 'Safe, certified mold removal and air quality restoration using EPA-approved methods to protect your health and property.',
    features: ['Mold testing', 'Containment setup', 'Safe removal', 'Air quality testing'],
    path: '/services',
    img: 'https://readdy.ai/api/search-image?query=mold%20remediation%20specialist%20in%20protective%20suit%20treating%20mold%20on%20walls%20with%20professional%20equipment%2C%20clean%20containment%20area%2C%20professional%20restoration%20service&width=800&height=500&seq=allsvc005&orientation=landscape',
    color: 'from-green-600 to-green-800',
  },
  {
    icon: 'ri-fire-fill',
    title: 'Fire & Smoke Damage',
    desc: 'Complete fire and smoke damage restoration including soot removal, odor elimination, and full structural repairs to return your property to normal.',
    features: ['Soot removal', 'Odor elimination', 'Smoke damage repair', 'Content restoration'],
    path: '/services',
    img: 'https://readdy.ai/api/search-image?query=fire%20damage%20restoration%20team%20cleaning%20soot%20and%20smoke%20damage%20from%20walls%20and%20ceiling%2C%20professional%20restoration%20equipment%2C%20clean%20workspace&width=800&height=500&seq=allsvc006&orientation=landscape',
    color: 'from-orange-600 to-orange-800',
  },
];

const servicesSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Restoration Services | Mitigation Restoration Services Houston TX',
    description: 'Complete restoration solutions including emergency cleanup, property restoration, pipe leak repair, mold remediation, and fire damage restoration in Houston TX.',
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Emergency Cleanup', url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/emergency-cleanup` },
        { '@type': 'ListItem', position: 2, name: 'Property Restoration', url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/property-restoration` },
        { '@type': 'ListItem', position: 3, name: 'Pipe Leak & Drainage', url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/pipe-leak-drainage` },
        { '@type': 'ListItem', position: 4, name: 'Damage Assessment' },
        { '@type': 'ListItem', position: 5, name: 'Mold Remediation' },
        { '@type': 'ListItem', position: 6, name: 'Fire & Smoke Damage Restoration' },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/` },
      { '@type': 'ListItem', position: 2, name: 'Services', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services` },
    ],
  },
];

export default function ServicesPage() {
  return (
    <div className="min-h-screen">
      <SeoHead
        title="Restoration Services | Emergency Cleanup, Mold & Fire Damage Houston TX"
        description="Complete restoration solutions in Houston TX: emergency cleanup, property restoration, pipe leak repair, mold remediation, and fire & smoke damage restoration. Certified team, 60-min response."
        keywords="restoration services Houston, emergency cleanup, mold remediation, fire damage restoration, pipe leak repair"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services`}
        schema={servicesSchema}
      />
      <Navbar />

      {/* Page Hero */}
      <section className="relative pt-32 pb-20 bg-[#1E3A5F]">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://readdy.ai/api/search-image?query=abstract%20geometric%20pattern%20texture%20for%20professional%20services%20company%20background%2C%20dark%20navy%20blue%2C%20subtle%20lines&width=1920&height=400&seq=svchero001&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-flex items-center gap-2 bg-[#DC2626] text-white text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-widest mb-4">
            <i className="ri-service-fill"></i>
            Our Services
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Complete Restoration Solutions
          </h1>
          <p className="text-white/70 text-base max-w-xl mx-auto">
            From emergency response to full property restoration — we handle every aspect of damage mitigation with certified expertise.
          </p>
        </div>
      </section>

      {/* Services List */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="space-y-12">
            {allServices.map((svc, idx) => (
              <div
                key={svc.title}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}
              >
                <div className={`relative rounded-2xl overflow-hidden h-72 ${idx % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <img
                    src={svc.img}
                    alt={svc.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className={`absolute inset-0 bg-gradient-to-br ${svc.color} opacity-20`}></div>
                </div>
                <div className={idx % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#DC2626]/10 mb-4">
                    <i className={`${svc.icon} text-[#DC2626] text-2xl`}></i>
                  </div>
                  <h2 className="text-2xl font-extrabold text-[#1E3A5F] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {svc.title}
                  </h2>
                  <p className="text-gray-500 text-sm leading-relaxed mb-5">{svc.desc}</p>
                  <ul className="grid grid-cols-2 gap-2 mb-6">
                    {svc.features.map((f) => (
                      <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                        <i className="ri-checkbox-circle-fill text-[#DC2626] text-base"></i>
                        {f}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={svc.path}
                    className="inline-flex items-center gap-2 bg-[#1E3A5F] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#DC2626] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Learn More <i className="ri-arrow-right-line"></i>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1E3A5F] text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h3 className="text-2xl font-extrabold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Ready to Get Started?
          </h3>
          <p className="text-white/70 text-sm mb-6">Contact our team today for a free assessment and rapid response.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+18005551234"
              className="inline-flex items-center justify-center gap-2 bg-[#DC2626] text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill"></i>
              Call Now
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-white hover:text-[#1E3A5F] transition-colors cursor-pointer whitespace-nowrap"
            >
              Get Free Assessment
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
